/**
 * CSV download shared by the report pages.
 *
 * Hand-rolled versions of this were copy-pasted across several report
 * components; the Figma Make export added two more that quoted naively (a name
 * containing a quote broke the row) and omitted the BOM. Prefer this.
 */

/**
 * Report-download telemetry.
 *
 * WHY A REGISTERED EMITTER RATHER THAN A PARAMETER. Twenty components call
 * downloadCsv, and coach activation needs to know a report was downloaded
 * regardless of which. Threading a callback through all twenty means twenty
 * chances to forget one, and a report added next month would silently not be
 * counted. Emitting from the one shared function counts every download, present
 * and future, from a single place.
 *
 * The indirection exists because downloadCsv is a plain function and the
 * reporting client is a hook — logEvent closes over the active product and the
 * user store, so it cannot be imported here. useReporting registers the
 * implementation; until it does, this is a no-op.
 *
 * Swallows its own errors on purpose: a telemetry failure must never stop a file
 * the user asked for from downloading.
 */
type DownloadEmitter = (filename: string, rowCount: number) => void;

let emitter: DownloadEmitter | null = null;

export const setReportDownloadEmitter = (fn: DownloadEmitter | null): void => {
  emitter = fn;
};

/**
 * Unregister, but only if `fn` is still the registered emitter.
 *
 * An unconditional clear on unmount is wrong here: React can mount the
 * replacement before tearing down the old instance (StrictMode's double
 * invoke, HMR, a remount), and the outgoing cleanup would then wipe the
 * incoming registration and leave downloads silently unreported. Comparing
 * identity first makes the teardown safe in any ordering.
 */
export const clearReportDownloadEmitter = (fn: DownloadEmitter): void => {
  if (emitter === fn) emitter = null;
};

const notifyDownload = (filename: string, rowCount: number): void => {
  try {
    emitter?.(filename, rowCount);
  } catch {
    /* telemetry must not break the download */
  }
};

/**
 * A cell that is quoted even when RFC 4180 would not require it, so the value
 * reads as text rather than as a number.
 *
 * Caveat worth knowing: Excel infers a column's type from the value, not from
 * the quoting, so this does NOT stop it dropping the leading zeros of an id
 * like 00123. Only the `="00123"` formula does that, and it litters the file
 * for every other consumer. Quoting is for parsers, not for Excel.
 */
class QuotedCell {
  constructor(readonly text: string) {}
}

/** Mark a value to be quoted in the output regardless of its content. */
export const quoted = (value: unknown): QuotedCell =>
  new QuotedCell(value == null ? "" : String(value));

/** RFC 4180 quoting: wrap when needed, and double any embedded quote. */
const escapeCell = (value: unknown): string => {
  if (value instanceof QuotedCell) {
    return `"${value.text.replace(/"/g, '""')}"`;
  }
  const text = value == null ? "" : String(value);
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

/** Exported for tests; the download path below is the normal entry point. */
export const toCsv = (headers: string[], rows: unknown[][]): string =>
  [headers, ...rows].map((row) => row.map(escapeCell).join(",")).join("\r\n");

/** Turn a filter scope into a filename-safe slug, e.g. "Lincoln MS" -> "lincoln-ms". */
const slugify = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

/**
 * Build a `name_part_part.csv` filename from a base name and any scope parts,
 * skipping empty parts.
 */
export const csvFilename = (
  base: string,
  ...parts: (string | null | undefined)[]
): string =>
  [base, ...parts.filter(Boolean).map((p) => slugify(p as string))]
    .filter(Boolean)
    .join("_") + ".csv";

export const downloadCsv = (
  filename: string,
  headers: string[],
  rows: unknown[][],
): void => {
  // Excel needs the UTF-8 BOM to avoid mojibake on non-ASCII names. Written as
  // an escape rather than the literal character: the literal is invisible in a
  // diff, which reads as a bare "" to anyone reviewing it (and to review bots,
  // which have twice filed it as a missing BOM), and is easy to strip by
  // accident. Identical bytes either way.
  const blob = new Blob(["\uFEFF", toCsv(headers, rows)], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  // Firefox honours `download` only for an anchor that is in the document, and
  // revoking synchronously after click() can race the browser's read of the
  // blob — so append, click, then revoke on the next tick.
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 0);

  // After the download is underway, so a throw here cannot cost the user a file.
  notifyDownload(filename, rows.length);
};
